/**
 * RecommendationsView (P193) -- the "so what?" inbox.
 *
 * A prioritised, grounded remediation list across all three diagnostic
 * pillars (assessment gaps, IBS/MVB fragility, scenario breaches). Each
 * card explains WHY (cited rationale), the IMPACT (severity + MVB), and
 * the ACTION -- a CTA routing to the existing surface where you act
 * (CAP workflow / war-room / fragility radar). Decision-support: nothing
 * mutates from here.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { isolateValue } from "shared/i18n";
import { statusLabel } from "shared/vocab/workflowState";
import { Link, useNavigate } from "react-router-dom";

import { MarsadIcon } from "shared/components/MarsadIcon";
import type { Recommendation } from "shared/api/types";
import { hasModule } from "shared/api/authority";
import { useMe } from "shared/api/useMe";
import { formatSAR } from "shared/money/money";
import {
  useDraftCapFromControl,
  useRecommendations,
} from "shared/recommendations/useRecommendations";
import { refusalMessage } from "shared/api/refusalMessage";
import { useToast } from "shared/toast/ToastProvider";

const SEVERITY_PILL: Record<string, string> = {
  critical: "bg-rag-redBg text-rag-red",
  high: "bg-rag-amberBg text-rag-amber",
  medium: "bg-slate-100 text-slate-600",
  low: "bg-slate-100 text-slate-500",
};

// action_kind -> where the operator goes to act on it.
//
// E4: these were all generic. "Review" on "Harden Card payments at
// POS" landed on the fragility radar -- the whole estate, ranked --
// leaving the reader to find the row the recommendation was about.
// Every recommendation carries `action_ref`: the IBS it is about, or
// the scenario that breached. Use it.
const ACTION_ROUTE: Record<string, string> = {
  draft_cap: "/framework/assessments",
  war_room: "/twin/war-room",
  fragility: "/twin/fragility",
  review: "/twin/fragility",
};

// A5 -- route by what the reference NAMES, not by the card's flavour.
// "review" and "fragility" are both ibs_risk recommendations, so both
// were sent to /twin/ibs/{ref}; but a concentration card names the
// service or vendor the estate leans on, not an IBS, and ten of them
// on one screen opened "Entity not found. Check the URL -- this id may
// have been deleted." with "Capability map" as the only way out.
const REF_KIND_ROUTE: Record<string, string> = {
  ibs_service: "/twin/ibs",
  service: "/twin/service",
  vendor: "/twin/vendor",
  capability: "/twin/capability",
  configuration_item: "/twin/ci",
};

export function actionRoute(
  kind: string,
  ref: string | null,
  refKind?: string | null,
): string {
  if (ref) {
    // A scenario breach is about one scenario; both surfaces read the
    // id from the URL.
    if (kind === "war_room") return `/twin/war-room?scenario=${ref}`;
    if (kind === "review" || kind === "fragility") {
      const base = refKind ? REF_KIND_ROUTE[refKind] : undefined;
      // No kind, or a kind with no entity page: fall back to the
      // radar filtered to the node, which renders the subject even
      // when we cannot open it directly. Never a detail page we have
      // only guessed at.
      return base ? `${base}/${ref}` : `/twin/fragility?node=${ref}`;
    }
  }
  return ACTION_ROUTE[kind] ?? "/";
}

type SourceFilter = "all" | "assessment_gap" | "ibs_risk" | "scenario_breach";

function RecommendationCardRow({
  rec,
  canDraft,
  canOpenCompliance,
  onDraft,
  drafting,
}: {
  rec: Recommendation;
  canDraft: boolean;
  /** Round 17 Q2 -- false for a seat without the compliance module: the
   *  assessments page refuses it, so the card says who acts instead. */
  canOpenCompliance: boolean;
  onDraft: (controlId: string) => void;
  drafting: boolean;
}) {
  const { t, i18n } = useTranslation();
  // P194: a draft_cap recommendation, for a preparer/admin, becomes a real
  // "Draft remediation CAP" button (creates + navigates). Otherwise (or for
  // other action kinds) it's a Link to the surface where you act.
  const canDraftHere =
    rec.action_kind === "draft_cap" && canDraft && rec.action_ref !== null;
  return (
    <article
      data-testid={`rec-${rec.id}`}
      data-source={rec.source}
      className="rounded-lg border border-slate-200 bg-white p-4 space-y-2"
    >
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {/* F (round 8) -- rendered from the key where the server sent
              one, so the sentence reaches the reader in their own
              language. Falls back to the English the server composed:
              a missing key must degrade to the sentence we have, not
              to a blank card. */}
          <h3 className="font-semibold text-slate-900" dir="auto">
            {rec.title_key ? t(rec.title_key, rec.title_params ?? {}) : rec.title}
          </h3>
          <p className="mt-0.5 text-sm text-slate-600" dir="auto">
            {/* Round 9 G -- `status` arrives as the column VALUE, so
                the words are chosen here. It used to arrive as
                "not compliant", which is an English run inside
                whatever sentence the catalogue supplies. */}
            {rec.rationale_key
              ? t(rec.rationale_key, {
                  ...(rec.rationale_params ?? {}),
                  // `status` is the one param that is a VALUE to be
                  // named; the rest are data the sentence prints.
                  ...(typeof rec.rationale_params?.status === "string"
                    ? { status: statusLabel(rec.rationale_params.status) }
                    : {}),
                })
              : rec.rationale}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {rec.revenue_at_risk_sar !== null && (
            <span
              data-testid="rec-revenue"
              className="rounded bg-brand-navy/5 px-1.5 py-0.5 text-[10px] font-semibold tracking-wide text-brand-navy"
            >
              {t("recommendations.revenueAtRisk", {
                sar: formatSAR(rec.revenue_at_risk_sar),
              })}
            </span>
          )}
          {rec.mvb_related && (
            <span className="rounded bg-brand-brass/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-brand-brassDark">
              {t("screens.recommendationsView.mvb")}
            </span>
          )}
          <span
            data-testid="rec-severity"
            className={`rounded px-2 py-0.5 text-xs font-medium ${SEVERITY_PILL[rec.severity] ?? ""}`}
          >
            {t(`recommendations.severity.${rec.severity}`)}
          </span>
        </div>
      </header>

      <p className="text-sm text-slate-800">
        {t("recommendations.soWhatLine", {
          action: rec.action_key
            ? t(rec.action_key, rec.action_params ?? {})
            : rec.suggested_action,
        })}
      </p>

      {rec.citations.length > 0 && (
        <p className="text-[11px] text-slate-400">
          {t("recommendations.basedOn")}{" "}
          {/* Round 9 G -- compose the citation here, in the reader's
              language.
              "based on: 3.1.1-CR7 (Cyber Security Governance -- non
              compliant)" was assembled server-side, so every word
              after the reference stayed English however good the
              catalogue got: the reader's language cannot reach a
              sentence that was finished before it left the backend.
              Falls back to the composed English where a citation
              carries no key -- a missing translation should read as
              English, not as an empty parenthesis. */}
          {/* Round 10 E1 -- and the REFERENCE is a name.
              This built one string with `${c.ref} (${detail})` and
              joined it, so the entity name arrived bare and unguarded
              inside an Arabic sentence -- one template repeated across
              thirty cards, and the single reason that page counted 141
              mixed runs. A name interpolated in JS never reaches
              `isolateValue`, which only sees what passes through the
              catalogue. `<bdi>` isolates it where it is rendered. */}
          {rec.citations.map((c, i) => {
            const detail = c.detail_key
              ? t(c.detail_key, {
                  ...(c.detail_params ?? {}),
                  // The status arrives as a VALUE; the words are
                  // ours to choose, in whichever language.
                  // `status` is the only param that is a VALUE to be
                  // named; the rest are data the sentence prints.
                  ...(typeof c.detail_params?.status === "string"
                    ? { status: statusLabel(c.detail_params.status) }
                    : {}),
                })
              : c.detail;
            return (
              <span key={`${c.ref}-${String(i)}`} data-testid="rec-citation">
                {i > 0 && "; "}
                {/* Round 11 E1 -- isolate with CHARACTERS, the way its
                    neighbours in this same card already are.
                    Round 10 put `<bdi>` here and the rendering has been
                    correct ever since. What was not correct is that
                    `<bdi>` is an ELEMENT: it inserts no text, so the
                    name is still a bare Latin run inside an Arabic
                    sentence to anything reading the sentence rather
                    than the DOM -- a screen reader following the text,
                    a copied line, a PNG export, and the walk's own
                    measurement, which counted 314 isolate characters
                    elsewhere on this page and none here.
                    Every neighbouring value gets U+2068/U+2069 from
                    `isolateValue` on its way through the catalogue.
                    A value interpolated in JS never passes through it,
                    so it is applied here by hand. `<bdi>` stays: it
                    costs nothing and still carries the isolation where
                    the characters are stripped. */}
                <bdi>{isolateValue(c.ref, i18n.language)}</bdi>
                {" ("}
                {/* A detail that came from the catalogue is already in
                    the reader's language and already carries isolates
                    around its own parameters. One that did not is
                    English the server composed, and needs the same
                    guard the reference does. */}
                <bdi>{c.detail_key ? detail : isolateValue(detail, i18n.language)}</bdi>
                {")"}
              </span>
            );
          })}
        </p>
      )}

      <div className="flex items-center justify-between pt-1">
        <span className="font-mono text-[10px] text-slate-400">
          {t("recommendations.sourceAndPriority", {
            source: t(`recommendations.source.${rec.source}`),
            score: rec.priority_score,
          })}
        </span>
        {canDraftHere ? (
          <button
            type="button"
            data-testid="rec-draft-cap"
            onClick={() => onDraft(rec.action_ref as string)}
            disabled={drafting}
            className="rounded-md bg-brand-brass px-2.5 py-1 text-xs font-medium text-brand-navy hover:bg-brand-brassDark disabled:opacity-60"
          >
            {drafting
              ? t("recommendations.drafting")
              : t("recommendations.action.draft_cap_do")}
          </button>
        ) : rec.action_kind === "draft_cap" && !canOpenCompliance ? (
          <span data-testid="rec-action-elsewhere" className="text-xs text-slate-500">
            {t("recommendations.action.draft_cap_elsewhere")}
          </span>
        ) : (
          <Link
            to={actionRoute(rec.action_kind, rec.action_ref, rec.action_ref_kind)}
            data-testid="rec-action"
            className="rounded-md border border-brand-brass/40 bg-brand-brass/5 px-2.5 py-1 text-xs font-medium text-brand-brassDark hover:bg-brand-brass/10"
          >
            {t(`recommendations.action.${rec.action_kind}`)}
          </Link>
        )}
      </div>
    </article>
  );
}

export function RecommendationsView(): JSX.Element {
  const { t } = useTranslation();
  const report = useRecommendations(50);
  const me = useMe();
  const navigate = useNavigate();
  const toast = useToast();
  const draft = useDraftCapFromControl();
  const [filter, setFilter] = useState<SourceFilter>("all");

  const canDraft = Boolean(
    me.data?.is_admin || me.data?.positions.includes("preparer"),
  );
  const onDraft = (controlId: string) => {
    draft.mutate(
      { controlId, withEngine: true },
      {
        onSuccess: (r) => {
          const message =
            r.created === false
              ? t("recommendations.reusedDraft")
              : r.actions != null
                ? t("recommendations.draftedWithActions", { n: r.actions })
                : r.engineOff
                  ? t("recommendations.draftedNoEngine")
                  : r.engineRefused
                    ? t("recommendations.draftedEngineRefused")
                    : t("recommendations.drafted");
          // ROUND 15 A10 -- "Remediation CAP drafted" was the whole story
          // when the Engine had drafted nothing onto it. The plan exists;
          // the actions do not; the toast says both.
          toast.push({ tone: r.engineRefused ? "warning" : "success", message });
          navigate(`/framework/caps/${r.cap_id}`);
        },
        onError: (e) => toast.push({ tone: "error", message: refusalMessage(t, e) }),
      },
    );
  };

  const all = report.data?.recommendations ?? [];
  const shown = filter === "all" ? all : all.filter((r) => r.source === filter);

  const FILTERS: SourceFilter[] = [
    "all",
    "assessment_gap",
    "ibs_risk",
    "scenario_breach",
  ];

  return (
    <section data-testid="recommendations-view" className="flex flex-col gap-4">
      <header>
        <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-slate-400">
          {t("recommendations.kicker")}
        </p>
        <h1 className="font-display text-2xl text-slate-900">
          <MarsadIcon
            name="recommendation"
            size={22}
            className="me-2 inline-block align-[-0.12em] text-brand-brassDark"
          />
          {t("recommendations.heading")}
        </h1>
        <p className="mt-1 max-w-3xl text-sm text-slate-600">
          {t("recommendations.intro")}
        </p>
      </header>

      {report.isLoading && (
        <p data-testid="recommendations-loading" className="text-sm text-slate-500">
          {t("recommendations.loading")}
        </p>
      )}
      {report.isError && (
        <p data-testid="recommendations-error" className="text-sm text-rag-red">
          {t("recommendations.error")}
        </p>
      )}

      {report.data && (
        <>
          <div
            data-testid="recommendations-summary"
            className="flex flex-wrap gap-2 text-sm"
          >
            <span className="rounded bg-rag-redBg px-2 py-0.5 font-medium text-rag-red">
              {t("recommendations.criticalCount", {
                n: report.data.by_severity.critical ?? 0,
              })}
            </span>
            <span className="rounded bg-rag-amberBg px-2 py-0.5 font-medium text-rag-amber">
              {t("recommendations.highCount", { n: report.data.by_severity.high ?? 0 })}
            </span>
            <span className="rounded bg-slate-100 px-2 py-0.5 font-medium text-slate-600">
              {t("recommendations.totalCount", { n: report.data.total })}
            </span>
          </div>

          <div className="flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <button
                key={f}
                type="button"
                data-testid={`rec-filter-${f}`}
                onClick={() => setFilter(f)}
                className={`rounded-md border px-2.5 py-1 text-xs ${
                  filter === f
                    ? "border-brand-brass/50 bg-brand-brass/5 text-brand-brassDark"
                    : "border-slate-200 bg-white text-slate-600 hover:border-brand-brass/30"
                }`}
              >
                {t(`recommendations.filter.${f}`)}
              </button>
            ))}
          </div>

          {shown.length === 0 ? (
            <p data-testid="recommendations-empty" className="text-sm text-slate-500">
              {t("recommendations.empty")}
            </p>
          ) : (
            <div className="flex flex-col gap-3">
              {shown.map((r) => (
                <RecommendationCardRow
                  key={r.id}
                  rec={r}
                  canDraft={canDraft}
                  canOpenCompliance={hasModule(me.data, "framework")}
                  onDraft={onDraft}
                  drafting={draft.isPending}
                />
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
}
